import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  FiAlignJustify,
  FiArrowLeft,
  FiDatabase,
  FiExternalLink,
  FiEye,
  FiFileText,
  FiGrid,
  FiHash,
  FiLayers,
  FiList,
  FiLock,
  FiMapPin,
  FiPackage,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTarget,
  FiX
} from 'react-icons/fi';
import {
  assetsApi,
  ipfsApi,
  legalReviewsApi,
  mintRequestsApi,
  tokenizersApi
} from '../../api/smartTokenizerApi';
import {
  formatEnumLabel,
  formatSectorLabel,
  isMappedAssetClassErc721,
  mapAssetToFrontend,
  mapSectorToCategory,
  TOKENIZER_PLATFORM_SECTORS
} from '../../api/smartTokenizerMappers';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useClientConfig } from '../../contexts/ClientConfigContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import BatchMintingWizard from '../AssetClassManager/BatchMintingWizard';
import VaultFractionalizationModal from './VaultFractionalizationModal';
import { getStoredSession } from '../../api/authStorage';
import {
  assetDocumentRowKey,
  buildAssetDocumentPreviewBlob,
  openAssetDocumentObjectUrl
} from '../../utils/assetDocumentPreview';
import { SearchableFilterSelect } from '../Admin/SearchableFilterSelect';
import AssetCreationWizard from './AssetCreationWizard';

const ManagerContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
  color: #fff;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #9bd6ff;
  text-decoration: none;
  margin-bottom: 1.5rem;
  font-weight: 500;
  &:hover {
    color: #c4e4ff;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 2.25rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.75rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const HeroSubtitle = styled.p`
  font-size: 1.05rem;
  color: #a0a0a0;
  max-width: 820px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Gate = styled.div`
  text-align: center;
  padding: 3rem;
  color: #fecaca;
  background: rgba(255, 80, 80, 0.08);
  border-radius: 16px;
  border: 1px solid rgba(255, 100, 100, 0.25);
`;

const Notice = styled.div`
  background: rgba(79, 172, 254, 0.12);
  border: 1px solid rgba(79, 172, 254, 0.35);
  color: #cfe9ff;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  line-height: 1.5;
  font-size: 0.95rem;
`;

const CreateAssetWizardModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 2rem;
`;

const CreateAssetWizardModalContent = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const TokenizeWizardModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1.5rem;
`;

const TokenizeWizardModalContent = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: 960px;
  width: 100%;
  max-height: 92vh;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const StatsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatTile = styled.div`
  padding: 1rem 1.15rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  text-align: center;
  .v {
    font-size: 1.5rem;
    font-weight: 800;
    color: #4facfe;
  }
  .l {
    font-size: 0.75rem;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: 0.25rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  .icon {
    color: #4facfe;
  }
`;

const ActionsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${(p) => (p.$primary ? 'linear-gradient(135deg, #4facfe, #00f2fe)' : 'rgba(255, 255, 255, 0.1)')};
  color: #fff;
  border: ${(p) => (p.$primary ? 'none' : '1px solid rgba(255, 255, 255, 0.2)')};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  &:hover {
    transform: translateY(-1px);
    opacity: ${(p) => (p.$primary ? 0.95 : 1)};
    background: ${(p) =>
      p.$primary ? 'linear-gradient(135deg, #3d8bfe, #00d4fe)' : 'rgba(255, 255, 255, 0.16)'};
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`;

const FilterSearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 220px;
  max-width: 400px;
  color: #94a3b8;
`;

const FilterTextInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.6rem 0.75rem;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  &::placeholder {
    color: #64748b;
  }
  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 160px;
  label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
  }
`;

const FilterMeta = styled.span`
  font-size: 0.85rem;
  color: #64748b;
  margin-left: auto;
  align-self: center;
`;

const RegistryToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
`;

const LayoutLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
`;

const ViewToggleGroup = styled.div`
  display: inline-flex;
  align-items: stretch;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.28);
  overflow: hidden;
`;

const ViewToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.8rem;
  border: none;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  background: ${(p) =>
    p.$active ? 'linear-gradient(135deg, rgba(79, 172, 254, 0.32), rgba(0, 242, 254, 0.12))' : 'transparent'};
  color: ${(p) => (p.$active ? '#e0f2fe' : '#94a3b8')};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }

  &:hover {
    color: #e2e8f0;
    background: ${(p) => (p.$active ? undefined : 'rgba(79, 172, 254, 0.1)')};
  }

  svg {
    flex-shrink: 0;
    opacity: ${(p) => (p.$active ? 1 : 0.75)};
  }
`;

const DataTableWrap = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
  overflow-x: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const DataTable = styled.table`
  width: 100%;
  min-width: 1040px;
  border-collapse: collapse;
  font-size: 0.86rem;

  th,
  td {
    text-align: left;
    padding: 0.8rem 0.95rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;
  }

  th {
    font-size: 0.66rem;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #94a3b8;
    font-weight: 700;
    background: rgba(0, 0, 0, 0.38);
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr {
    transition: background 0.15s;
  }

  tbody tr:hover {
    background: rgba(79, 172, 254, 0.07);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const TableAssetTitle = styled.div`
  font-weight: 700;
  color: #f8fafc;
  margin-bottom: 0.15rem;
`;

const TableMuted = styled.span`
  font-size: 0.72rem;
  color: #64748b;
  font-family: ui-monospace, monospace;
`;

const TableActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: flex-end;
`;

const TableIconBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 7px;
  border: 1px solid rgba(79, 172, 254, 0.35);
  background: rgba(79, 172, 254, 0.12);
  color: #9bd6ff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) {
    background: rgba(79, 172, 254, 0.22);
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const CompactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const CompactRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.65rem 1rem;
  align-items: center;
  padding: 0.85rem 1.1rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  &:hover {
    border-color: rgba(79, 172, 254, 0.38);
    background: rgba(79, 172, 254, 0.06);
    box-shadow: 0 4px 20px rgba(79, 172, 254, 0.08);
  }

  .cr-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.35rem;
  }

  .cr-title {
    font-weight: 700;
    color: #fff;
    font-size: 0.98rem;
  }

  .cr-meta {
    font-size: 0.78rem;
    color: #94a3b8;
    line-height: 1.55;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
  }

  .cr-dot {
    opacity: 0.45;
  }

  .cr-actions {
    display: flex;
    gap: 0.4rem;
    flex-shrink: 0;
    justify-content: flex-end;
  }
`;

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 1.5rem;
`;

const AssetCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.75rem;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-3px);
    border-color: #4facfe;
    box-shadow: 0 10px 30px rgba(79, 172, 254, 0.12);
  }
`;

const CardTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const CardMeta = styled.div`
  font-size: 0.88rem;
  color: #94a3b8;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

const Badge = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.2);
  color: #cbd5e1;
  &.accent {
    color: #7dd3fc;
    border-color: rgba(125, 211, 252, 0.35);
    background: rgba(56, 189, 248, 0.1);
  }
  &.ok {
    color: #86efac;
    border-color: rgba(134, 239, 172, 0.35);
  }
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  background: rgba(79, 172, 254, 0.15);
  color: #9bd6ff;
  border: 1px solid rgba(79, 172, 254, 0.35);
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const CardBtn = styled.button`
  flex: 1;
  padding: 0.65rem;
  border-radius: 8px;
  border: 1px solid rgba(79, 172, 254, 0.35);
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  color: #fff;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  &:hover {
    opacity: 0.95;
  }
`;

const OutlineCardBtn = styled.button`
  flex: 1;
  padding: 0.65rem;
  border-radius: 8px;
  border: 1px solid rgba(125, 211, 252, 0.45);
  background: rgba(79, 172, 254, 0.08);
  color: #bae6fd;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  &:hover {
    background: rgba(79, 172, 254, 0.16);
  }
`;

const IconGhost = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.06);
  color: #9bd6ff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) {
    background: rgba(79, 172, 254, 0.15);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.25rem;
`;

const ModalCard = styled(motion.div)`
  background: linear-gradient(145deg, #151528 0%, #1a1a38 100%);
  border: 1px solid rgba(79, 172, 254, 0.28);
  border-radius: 20px;
  max-width: 760px;
  width: 100%;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  h2 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 700;
  }
`;

const TabRow = styled.div`
  display: flex;
  gap: 0.35rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
`;

const TabBtn = styled.button`
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$on ? 'rgba(79, 172, 254, 0.5)' : 'transparent')};
  background: ${(p) => (p.$on ? 'rgba(79, 172, 254, 0.18)' : 'transparent')};
  color: ${(p) => (p.$on ? '#e0f2fe' : '#94a3b8')};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  &:hover {
    color: #e2e8f0;
  }
`;

const ModalBody = styled.div`
  padding: 1.15rem 1.25rem 1.35rem;
  overflow-y: auto;
`;

const DetailGrid = styled.dl`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 0.45rem 1rem;
  margin: 0 0 1rem 0;
  font-size: 0.88rem;
  dt {
    color: #94a3b8;
    margin: 0;
  }
  dd {
    margin: 0;
    color: #f1f5f9;
    word-break: break-word;
  }
`;

const JsonBlock = styled.pre`
  margin: 0;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 10px;
  font-size: 0.72rem;
  line-height: 1.45;
  overflow-x: auto;
  color: #c8e6c9;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const DocPanel = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
`;

const DocTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  th,
  td {
    text-align: left;
    padding: 0.65rem 0.75rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  }
  th {
    color: #94a3b8;
    font-weight: 700;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.25);
  }
  tr:last-child td {
    border-bottom: none;
  }
  td.doc-actions {
    white-space: nowrap;
    width: 1%;
  }
`;

const DocViewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #e0f2fe;
  background: rgba(56, 189, 248, 0.14);
  border: 1px solid rgba(56, 189, 248, 0.35);
  border-radius: 6px;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(56, 189, 248, 0.26);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ChainBtn = styled(DocViewBtn)``;

const pick = (obj, ...keys) => {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
};

const ASSET_STATUS_NAMES = [
  '',
  'Draft',
  'SubmittedForLegalReview',
  'UnderLegalReview',
  'ApprovedForTokenization',
  'Rejected',
  'MetadataPinnedToIpfs',
  'MintRequested',
  'MintingInProgress',
  'Minted',
  'VaultLockInProgress',
  'LockedInVault',
  'FractionalizationInProgress',
  'Fractionalized'
];

function assetStatusCanonical(status) {
  if (status === null || status === undefined) return 'Unknown';
  if (typeof status === 'number') {
    if (status >= 1 && status < ASSET_STATUS_NAMES.length) return ASSET_STATUS_NAMES[status];
    return `Unknown(${status})`;
  }
  const str = String(status).trim();
  if (!str) return 'Unknown';
  const exact = ASSET_STATUS_NAMES.find((n) => n === str);
  if (exact) return exact;
  const fold = str.replace(/\s+/g, '');
  const ci = ASSET_STATUS_NAMES.find((n) => n.toLowerCase() === fold.toLowerCase());
  if (ci) return ci;
  return str;
}

const LEGAL_NAMES = ['', 'PendingAssignment', 'InReview', 'Approved', 'Rejected'];

function legalStatusCanonical(s) {
  if (s === null || s === undefined) return '';
  if (typeof s === 'number') {
    return LEGAL_NAMES[s] || String(s);
  }
  return String(s).trim();
}

function legalReviewsOf(raw) {
  const l = raw?.legalReviews ?? raw?.LegalReviews;
  return Array.isArray(l) ? l : [];
}

function reviewerId(lr) {
  return String(lr?.assignedReviewerId ?? lr?.AssignedReviewerId ?? '').trim();
}

function summarizeLegalForCard(raw) {
  const lrs = legalReviewsOf(raw);
  if (lrs.length === 0) return 'No legal reviews';
  const parts = lrs.map((lr) => formatEnumLabel(legalStatusCanonical(lr.status ?? lr.Status)));
  return `${lrs.length} review${lrs.length === 1 ? '' : 's'} · ${parts.join(', ')}`;
}

const MINTED_OR_LATER = new Set([
  'Minted',
  'VaultLockInProgress',
  'LockedInVault',
  'FractionalizationInProgress',
  'Fractionalized'
]);

function registryShowsFractionalizeAction(mapped, isTokenizerOrAdmin) {
  if (!isTokenizerOrAdmin || !mapped) return false;
  if (!isMappedAssetClassErc721(mapped)) return false;
  const st = assetStatusCanonical(mapped.status);
  return MINTED_OR_LATER.has(st);
}

function fractionalizeRegistryButtonLabel(mapped) {
  const st = assetStatusCanonical(mapped.status);
  if (st === 'Minted') return 'Fractionalize NFT';
  if (st === 'Fractionalized') return 'Fractionalization details';
  return 'Continue fractionalization';
}

const REGISTRY_VIEW_STORAGE_KEY = 'tokenizerAssetsRegistryView';
const REGISTRY_VIEWS = ['gallery', 'table', 'compact'];

function readSavedRegistryView() {
  try {
    const v = localStorage.getItem(REGISTRY_VIEW_STORAGE_KEY);
    if (REGISTRY_VIEWS.includes(v)) return v;
  } catch {
    /* ignore */
  }
  return 'gallery';
}

function clipText(s, max) {
  const t = String(s || '');
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1))}…`;
}

function galleryAssetClassLine(mapped) {
  if (mapped.assetClassName) return { text: mapped.assetClassName, muted: false };
  if (mapped.assetClassId) {
    const id = String(mapped.assetClassId);
    return { text: id.length > 14 ? `${id.slice(0, 10)}…${id.slice(-4)}` : id, muted: true };
  }
  return { text: '—', muted: true };
}

function TokenizerMyAssets() {
  const { getAssetClassById } = useAssetClass();
  const { isAdmin, isAuthorizedTokenizer, currentIssuer } = useTokenIssuer();
  const { addressExplorerUrl, tokenExplorerUrl, transactionExplorerUrl, explorerConfigured } = useClientConfig();
  const signedIn = Boolean(getStoredSession()?.accessToken);
  const mayUsePage = isAdmin || isAuthorizedTokenizer;

  const [entries, setEntries] = useState([]);
  const [tokenizers, setTokenizers] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tokenizerFilter, setTokenizerFilter] = useState('');
  const [reviewerFilter, setReviewerFilter] = useState('all');
  const [legalStatusFilter, setLegalStatusFilter] = useState('');
  const [contractFilter, setContractFilter] = useState('all');
  const [metadataFilter, setMetadataFilter] = useState('all');
  const [docsFilter, setDocsFilter] = useState('all');
  const [mintStageFilter, setMintStageFilter] = useState('all');
  const [registryView, setRegistryView] = useState(readSavedRegistryView);

  const [detailModal, setDetailModal] = useState({
    open: false,
    loading: false,
    error: null,
    mapped: null,
    raw: null,
    mintedToken: null,
    mintedTokenLoading: false,
    mintedTokenError: null,
    tab: 'overview'
  });
  const [openingDocumentKey, setOpeningDocumentKey] = useState(null);
  const [nftExplorerLoadingId, setNftExplorerLoadingId] = useState(null);
  const [createAssetWizardOpen, setCreateAssetWizardOpen] = useState(false);
  const [createAssetWizardCategory, setCreateAssetWizardCategory] = useState(null);
  const [fractionalizeMapped, setFractionalizeMapped] = useState(null);
  const [detailVaultFrac, setDetailVaultFrac] = useState({
    loading: false,
    error: null,
    vaultLock: null,
    fractional: null
  });
  const [tokenizeModal, setTokenizeModal] = useState({
    open: false,
    loading: false,
    mapped: null,
    assetClass: null
  });

  const load = useCallback(async () => {
    if (!signedIn || !mayUsePage) return;
    setLoading(true);
    try {
      const revPromise = legalReviewsApi.assignableReviewers().catch(() => ({ data: [] }));
      if (isAdmin) {
        const [assetsRes, tokRes, revRes] = await Promise.all([
          assetsApi.list(),
          tokenizersApi.list(),
          revPromise
        ]);
        const data = Array.isArray(assetsRes.data) ? assetsRes.data : [];
        setEntries(data.map((raw) => ({ raw, mapped: mapAssetToFrontend(raw) })));
        setTokenizers(Array.isArray(tokRes.data) ? tokRes.data : []);
        setReviewers(Array.isArray(revRes.data) ? revRes.data : []);
      } else {
        const [assetsRes, revRes] = await Promise.all([assetsApi.list(), revPromise]);
        const data = Array.isArray(assetsRes.data) ? assetsRes.data : [];
        setEntries(data.map((raw) => ({ raw, mapped: mapAssetToFrontend(raw) })));
        setTokenizers([]);
        setReviewers(Array.isArray(revRes.data) ? revRes.data : []);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Failed to load assets');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [signedIn, mayUsePage, isAdmin]);

  useEffect(() => {
    if (signedIn && mayUsePage) load();
  }, [signedIn, mayUsePage, load]);

  const closeTokenizeWizard = useCallback(() => {
    setTokenizeModal({ open: false, loading: false, mapped: null, assetClass: null });
    load();
  }, [load]);

  const openTokenizeWizard = useCallback(
    async (mapped) => {
      if (!mapped?.assetClassId) {
        toast.error('This asset has no asset class assigned.');
        return;
      }
      setTokenizeModal({ open: true, loading: true, mapped, assetClass: null });
      try {
        const ac = await getAssetClassById(mapped.assetClassId, true);
        if (!ac) {
          throw new Error('Asset class not found');
        }
        setTokenizeModal((s) => ({ ...s, loading: false, assetClass: ac }));
      } catch (e) {
        toast.error(e.response?.data?.message || e.message || 'Could not load asset class');
        setTokenizeModal({ open: false, loading: false, mapped: null, assetClass: null });
      }
    },
    [getAssetClassById]
  );

  const fetchDetailVaultFracForMapped = useCallback(async (mapped) => {
    if (!mapped?.id) return;
    const st = assetStatusCanonical(mapped.status);
    if (!isMappedAssetClassErc721(mapped) || !MINTED_OR_LATER.has(st)) {
      setDetailVaultFrac({ loading: false, error: null, vaultLock: null, fractional: null });
      return;
    }
    setDetailVaultFrac((s) => ({ ...s, loading: true, error: null }));
    try {
      const vl = await assetsApi
        .getVaultLock(mapped.id)
        .then((r) => r.data)
        .catch((e) => {
          if (e.response?.status === 404) return null;
          throw e;
        });
      const fr = await assetsApi
        .getFractionalShares(mapped.id)
        .then((r) => r.data)
        .catch((e) => {
          if (e.response?.status === 404) return null;
          throw e;
        });
      setDetailVaultFrac({ loading: false, error: null, vaultLock: vl, fractional: fr });
    } catch (e) {
      setDetailVaultFrac({
        loading: false,
        error: e.response?.data?.message || e.message || 'Failed to load vault / fractionalization',
        vaultLock: null,
        fractional: null
      });
    }
  }, []);

  useEffect(() => {
    if (!detailModal.open || detailModal.tab !== 'onchain' || !detailModal.mapped) {
      return;
    }
    fetchDetailVaultFracForMapped(detailModal.mapped);
  }, [
    detailModal.open,
    detailModal.tab,
    detailModal.mapped?.id,
    detailModal.mapped?.status,
    detailModal.mapped?.tokenStandard,
    fetchDetailVaultFracForMapped
  ]);

  useEffect(() => {
    try {
      localStorage.setItem(REGISTRY_VIEW_STORAGE_KEY, registryView);
    } catch {
      /* ignore */
    }
  }, [registryView]);

  const reviewerLabel = useCallback(
    (userId) => {
      const id = String(userId || '');
      const r = reviewers.find((x) => String(x.userId ?? x.UserId ?? '') === id);
      if (!r) return id ? `${id.slice(0, 8)}…` : '—';
      const name = r.fullName ?? r.FullName ?? '';
      const email = r.email ?? r.Email ?? '';
      const org = r.organizationName ?? r.OrganizationName ?? '';
      return [name || email, org ? `· ${org}` : ''].filter(Boolean).join(' ');
    },
    [reviewers]
  );

  const tokenizerLabel = useCallback(
    (id) => {
      const t = tokenizers.find((x) => String(x.id ?? x.Id) === String(id));
      return pick(t, 'organizationName', 'OrganizationName') || String(id).slice(0, 8);
    },
    [tokenizers]
  );

  const resolveTokenizerDisplay = useCallback(
    (raw, mapped) => {
      if (isAdmin) return tokenizerLabel(mapped.authorizedTokenizerId);
      const tok = raw?.authorizedTokenizer ?? raw?.AuthorizedTokenizer;
      return (
        pick(tok, 'organizationName', 'OrganizationName') ||
        currentIssuer?.name ||
        String(mapped.authorizedTokenizerId || '').slice(0, 8) ||
        '—'
      );
    },
    [isAdmin, tokenizerLabel, currentIssuer?.name]
  );

  const openMintedAssetInExplorer = useCallback(
    async (assetId) => {
      if (!explorerConfigured) {
        toast.error('Block explorer URL is not configured.');
        return;
      }
      setNftExplorerLoadingId(assetId);
      try {
        const { data } = await mintRequestsApi.getByAssetId(assetId);
        const contract = String(data.contractAddress ?? data.ContractAddress ?? '').trim();
        const tokenId = String(data.tokenId ?? data.TokenId ?? '').trim();
        const url = tokenExplorerUrl(contract, tokenId);
        if (!url) {
          toast.error('Missing contract address for this mint.');
          return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (e) {
        if (e.response?.status === 404) {
          toast.error('No minted token on record for this asset yet.');
        } else {
          toast.error(e.response?.data?.message || e.message || 'Could not load minted token');
        }
      } finally {
        setNftExplorerLoadingId(null);
      }
    },
    [explorerConfigured, tokenExplorerUrl]
  );

  const closeCreateAssetWizard = useCallback(() => {
    setCreateAssetWizardOpen(false);
    setCreateAssetWizardCategory(null);
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return entries.filter(({ raw, mapped }) => {
      if (sectorFilter && mapSectorToCategory(mapped.sector) !== sectorFilter) return false;
      if (statusFilter && assetStatusCanonical(mapped.status) !== statusFilter) return false;
      if (isAdmin && tokenizerFilter && String(mapped.authorizedTokenizerId) !== tokenizerFilter) return false;

      if (reviewerFilter === 'unassigned') {
        const lrs = legalReviewsOf(raw);
        const hit = lrs.some((lr) => {
          const st = legalStatusCanonical(lr.status ?? lr.Status);
          const open = st === 'PendingAssignment' || st === 'InReview';
          return open && !reviewerId(lr);
        });
        if (!hit) return false;
      } else if (reviewerFilter !== 'all') {
        const id = reviewerFilter;
        const hit = legalReviewsOf(raw).some((lr) => reviewerId(lr) === id);
        if (!hit) return false;
      }

      if (legalStatusFilter) {
        const hit = legalReviewsOf(raw).some(
          (lr) => legalStatusCanonical(lr.status ?? lr.Status) === legalStatusFilter
        );
        if (!hit) return false;
      }

      if (contractFilter === 'deployed' && !mapped.contractAddress) return false;
      if (contractFilter === 'not_deployed' && mapped.contractAddress) return false;

      if (metadataFilter === 'yes' && !mapped.metadataIpfsCid) return false;
      if (metadataFilter === 'no' && mapped.metadataIpfsCid) return false;

      const docCount = Array.isArray(mapped.documents) ? mapped.documents.length : 0;
      if (docsFilter === 'yes' && docCount === 0) return false;
      if (docsFilter === 'no' && docCount > 0) return false;

      const st = assetStatusCanonical(mapped.status);
      if (mintStageFilter === 'minted' && !MINTED_OR_LATER.has(st)) return false;
      if (mintStageFilter === 'pre_mint' && MINTED_OR_LATER.has(st)) return false;

      if (!q) return true;
      const blob = [
        mapped.name,
        mapped.title,
        mapped.assetNumber,
        mapped.country,
        formatSectorLabel(mapped.sector),
        mapped.assetClassName,
        formatEnumLabel(mapped.status),
        resolveTokenizerDisplay(raw, mapped),
        String(mapped.id || '')
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [
    entries,
    searchQuery,
    sectorFilter,
    statusFilter,
    tokenizerFilter,
    reviewerFilter,
    legalStatusFilter,
    contractFilter,
    metadataFilter,
    docsFilter,
    mintStageFilter,
    isAdmin,
    resolveTokenizerDisplay
  ]);

  const stats = useMemo(() => {
    const deployed = entries.filter((e) => e.mapped.contractAddress).length;
    const withMeta = entries.filter((e) => e.mapped.metadataIpfsCid).length;
    const inLegal = entries.filter((e) =>
      legalReviewsOf(e.raw).some((lr) => {
        const st = legalStatusCanonical(lr.status ?? lr.Status);
        return st === 'PendingAssignment' || st === 'InReview';
      })
    ).length;
    const minted = entries.filter((e) => MINTED_OR_LATER.has(assetStatusCanonical(e.mapped.status))).length;
    return { total: entries.length, deployed, withMeta, inLegal, minted };
  }, [entries]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    for (const { mapped } of entries) {
      set.add(assetStatusCanonical(mapped.status));
    }
    return [...set].sort();
  }, [entries]);

  const sectorSelectOptions = useMemo(
    () => [
      { value: '', label: 'All sectors' },
      ...TOKENIZER_PLATFORM_SECTORS.map((s) => ({ value: s.id, label: s.name }))
    ],
    []
  );

  const statusSelectOptions = useMemo(
    () => [
      { value: '', label: 'All statuses' },
      ...statusOptions.map((s) => ({ value: s, label: formatEnumLabel(s) }))
    ],
    [statusOptions]
  );

  const tokenizerSelectOptions = useMemo(
    () => [
      { value: '', label: 'All tokenizers' },
      ...tokenizers.map((t) => {
        const id = String(t.id ?? t.Id ?? '');
        return { value: id, label: pick(t, 'organizationName', 'OrganizationName') || id };
      })
    ],
    [tokenizers]
  );

  const reviewerSelectOptions = useMemo(
    () => [
      { value: 'all', label: 'Any assignment' },
      { value: 'unassigned', label: 'Open reviews · unassigned' },
      ...reviewers
        .map((r) => {
          const id = String(r.userId ?? r.UserId ?? '').trim();
          if (!id) return null;
          return { value: id, label: reviewerLabel(id) };
        })
        .filter(Boolean)
    ],
    [reviewers, reviewerLabel]
  );

  const legalStatusSelectOptions = useMemo(
    () => [
      { value: '', label: 'Any review status' },
      ...['PendingAssignment', 'InReview', 'Approved', 'Rejected'].map((s) => ({
        value: s,
        label: formatEnumLabel(s)
      }))
    ],
    []
  );

  const contractSelectOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'deployed', label: 'Deployed on class' },
      { value: 'not_deployed', label: 'Not deployed' }
    ],
    []
  );

  const metadataSelectOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'yes', label: 'Pinned' },
      { value: 'no', label: 'Not pinned' }
    ],
    []
  );

  const docsSelectOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'yes', label: 'Has files' },
      { value: 'no', label: 'No files' }
    ],
    []
  );

  const mintStageSelectOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'pre_mint', label: 'Pre-mint workflow' },
      { value: 'minted', label: 'Minted or later' }
    ],
    []
  );

  const closeModal = () => {
    setOpeningDocumentKey(null);
    setDetailVaultFrac({ loading: false, error: null, vaultLock: null, fractional: null });
    setDetailModal({
      open: false,
      loading: false,
      error: null,
      mapped: null,
      raw: null,
      mintedToken: null,
      mintedTokenLoading: false,
      mintedTokenError: null,
      tab: 'overview'
    });
  };

  const openDetail = async (assetId) => {
    if (!assetId) return;
    setDetailModal({
      open: true,
      loading: true,
      error: null,
      mapped: null,
      raw: null,
      mintedToken: null,
      mintedTokenLoading: false,
      mintedTokenError: null,
      tab: 'overview'
    });
    try {
      const { data } = await assetsApi.getById(assetId);
      setDetailModal((m) => ({
        ...m,
        loading: false,
        mapped: mapAssetToFrontend(data),
        raw: data,
        mintedToken: null,
        mintedTokenLoading: true,
        mintedTokenError: null
      }));
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Failed to load asset';
      setDetailModal((m) => ({ ...m, loading: false, error: msg, mintedTokenLoading: false }));
      toast.error(msg);
      return;
    }
    try {
      const { data: mint } = await mintRequestsApi.getByAssetId(assetId);
      setDetailModal((m) => ({
        ...m,
        mintedToken: mint,
        mintedTokenLoading: false,
        mintedTokenError: null
      }));
    } catch (e) {
      if (e.response?.status === 404) {
        setDetailModal((m) => ({
          ...m,
          mintedToken: null,
          mintedTokenLoading: false,
          mintedTokenError: null
        }));
      } else {
        setDetailModal((m) => ({
          ...m,
          mintedToken: null,
          mintedTokenLoading: false,
          mintedTokenError: e.response?.data?.message || e.message
        }));
      }
    }
  };

  const openAssetDocument = useCallback(async (doc) => {
    const cid = String(doc?.ipfsCid ?? doc?.IpfsCid ?? '').trim();
    const rowKey = assetDocumentRowKey(doc);
    if (!cid) {
      toast.error('No IPFS CID for this document.');
      return;
    }
    setOpeningDocumentKey(rowKey);
    try {
      const response = await ipfsApi.downloadByCid(cid);
      const blob = await buildAssetDocumentPreviewBlob(response, doc);
      const url = URL.createObjectURL(blob);
      const opened = openAssetDocumentObjectUrl(url, blob.type || 'application/octet-stream', doc);
      if (!opened) {
        toast.error('Pop-up blocked. Allow pop-ups to view the document.');
        URL.revokeObjectURL(url);
        return;
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 600000);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Could not load document');
    } finally {
      setOpeningDocumentKey(null);
    }
  }, []);

  if (!signedIn) {
    return (
      <ManagerContainer>
        <Back to="/">
          <FiArrowLeft /> Back to dashboard
        </Back>
        <HeroSection>
          <HeroTitle>
            <FiDatabase /> Assets Registry
          </HeroTitle>
          <HeroSubtitle>
            Sign in with an authorized tokenizer operator or administrator account to browse assets, filter by workflow and
            legal state, and open details with on-chain links and document preview.
          </HeroSubtitle>
        </HeroSection>
        <Notice style={{ maxWidth: 720, margin: '0 auto' }}>
          Use the Login control in the header, then open Core Tokenizer to create or manage assets.
        </Notice>
      </ManagerContainer>
    );
  }

  if (!mayUsePage) {
    return (
      <ManagerContainer>
        <Back to="/">
          <FiArrowLeft /> Back to dashboard
        </Back>
        <HeroSection>
          <HeroTitle>
            <FiDatabase /> Assets Registry
          </HeroTitle>
          <HeroSubtitle>
            This registry is available to users with the Authorized tokenizer role (assigned to a program) or administrators.
          </HeroSubtitle>
        </HeroSection>
        <Gate style={{ maxWidth: 720, margin: '0 auto' }}>
          <h2>Access required</h2>
          <p>Your current roles do not include asset registry access via this page.</p>
        </Gate>
      </ManagerContainer>
    );
  }

  const dm = detailModal;
  const mayTokenizerOrAdmin = isAuthorizedTokenizer || isAdmin;
  const ac = dm.raw?.assetClass ?? dm.raw?.AssetClass;
  const tok = dm.raw?.authorizedTokenizer ?? dm.raw?.AuthorizedTokenizer;
  const meta = dm.mapped?.metadata || {};
  const docs = dm.mapped?.documents || [];
  const deployedContract = String(dm.mapped?.contractAddress || '').trim();
  const mint = dm.mintedToken;
  const mintContract = mint ? String(mint.contractAddress ?? mint.ContractAddress ?? '').trim() : '';
  const mintTokenId = mint ? String(mint.tokenId ?? mint.TokenId ?? '').trim() : '';
  const mintStandard = mint
    ? formatEnumLabel(String(mint.tokenStandard ?? mint.TokenStandard ?? ''))
    : '';
  const mintTx = mint ? String(mint.mintTransactionHash ?? mint.MintTransactionHash ?? '').trim() : '';
  const mintAmount = mint ? String(mint.amount ?? mint.Amount ?? '').trim() : '';
  const tokenPageContract = mintContract || deployedContract;

  return (
    <ManagerContainer>
      <Back to="/">
        <FiArrowLeft /> Back to dashboard
      </Back>

      <HeroSection>
        <HeroTitle>
          <FiDatabase /> Assets Registry
        </HeroTitle>
        <HeroSubtitle>
          {isAdmin
            ? 'Full registry across all authorized tokenizers. Filter by sector, workflow status, tokenizer, legal assignment and review state, then open any asset for metadata, on-chain contract and mint details, and document preview.'
            : `Assets registered under your authorized tokenizer${currentIssuer?.name ? ` (${currentIssuer.name})` : ''}. Filter by sector, workflow, legal assignment and review state, then open any asset for metadata, on-chain links, and documents. Create new assets from Tokenize Assets or the tokenizer home.`}
        </HeroSubtitle>
      </HeroSection>

      <StatsStrip>
        <StatTile>
          <div className="v">{stats.total}</div>
          <div className="l">Total assets</div>
        </StatTile>
        <StatTile>
          <div className="v">{stats.deployed}</div>
          <div className="l">Class contract deployed</div>
        </StatTile>
        <StatTile>
          <div className="v">{stats.withMeta}</div>
          <div className="l">Metadata on IPFS</div>
        </StatTile>
        <StatTile>
          <div className="v">{stats.inLegal}</div>
          <div className="l">Active legal reviews</div>
        </StatTile>
        <StatTile>
          <div className="v">{stats.minted}</div>
          <div className="l">Minted stage+</div>
        </StatTile>
      </StatsStrip>

      <ActionsSection>
        <SectionTitle style={{ marginBottom: 0 }}>
          <FiTarget className="icon" />
          Registry
        </SectionTitle>
        <RegistryToolbar>
          {(isAuthorizedTokenizer || isAdmin) && (
            <ActionButton
              type="button"
              $primary
              onClick={() => {
                setCreateAssetWizardCategory(null);
                setCreateAssetWizardOpen(true);
              }}
            >
              <FiPlus />
              Create New Asset
            </ActionButton>
          )}
          <LayoutLabel>View</LayoutLabel>
          <ViewToggleGroup role="group" aria-label="Asset list layout">
            <ViewToggleBtn
              type="button"
              $active={registryView === 'gallery'}
              onClick={() => setRegistryView('gallery')}
              title="Gallery — rich cards for scanning and context"
            >
              <FiGrid aria-hidden />
              Gallery
            </ViewToggleBtn>
            <ViewToggleBtn
              type="button"
              $active={registryView === 'table'}
              onClick={() => setRegistryView('table')}
              title="Table — dense columns for comparison and export mindset"
            >
              <FiList aria-hidden />
              Table
            </ViewToggleBtn>
            <ViewToggleBtn
              type="button"
              $active={registryView === 'compact'}
              onClick={() => setRegistryView('compact')}
              title="Compact — minimal rows for long lists"
            >
              <FiAlignJustify aria-hidden />
              Compact
            </ViewToggleBtn>
          </ViewToggleGroup>
          <ActionButton type="button" $primary onClick={load} disabled={loading}>
            <FiRefreshCw />
            {loading ? 'Loading…' : 'Refresh'}
          </ActionButton>
        </RegistryToolbar>
      </ActionsSection>

      <FilterBar>
        <FilterSearchWrap>
          <FiSearch aria-hidden />
          <FilterTextInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={
              isAdmin
                ? 'Search title, #, sector, tokenizer, country, id…'
                : 'Search title, #, sector, country, id…'
            }
            aria-label="Search assets"
          />
        </FilterSearchWrap>
        <FilterField>
          <label id="adm-ast-sector-lbl" htmlFor="adm-ast-sector">
            Sector
          </label>
          <SearchableFilterSelect
            id="adm-ast-sector"
            optionsListLabelledBy="adm-ast-sector-lbl"
            value={sectorFilter}
            onChange={setSectorFilter}
            options={sectorSelectOptions}
            placeholder="All sectors"
          />
        </FilterField>
        <FilterField>
          <label id="adm-ast-status-lbl" htmlFor="adm-ast-status">
            Asset status
          </label>
          <SearchableFilterSelect
            id="adm-ast-status"
            optionsListLabelledBy="adm-ast-status-lbl"
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusSelectOptions}
            placeholder="All statuses"
          />
        </FilterField>
        {isAdmin ? (
          <FilterField>
            <label id="tok-ast-tok-lbl" htmlFor="tok-ast-tok">
              Tokenizer
            </label>
            <SearchableFilterSelect
              id="tok-ast-tok"
              optionsListLabelledBy="tok-ast-tok-lbl"
              value={tokenizerFilter}
              onChange={setTokenizerFilter}
              options={tokenizerSelectOptions}
              placeholder="All tokenizers"
              maxWidth="320px"
            />
          </FilterField>
        ) : null}
        <FilterField>
          <label id="adm-ast-rev-lbl" htmlFor="adm-ast-rev">
            Legal reviewer
          </label>
          <SearchableFilterSelect
            id="adm-ast-rev"
            optionsListLabelledBy="adm-ast-rev-lbl"
            value={reviewerFilter}
            onChange={setReviewerFilter}
            options={reviewerSelectOptions}
            placeholder="Any assignment"
            maxWidth="320px"
          />
        </FilterField>
        <FilterField>
          <label id="adm-ast-lr-lbl" htmlFor="adm-ast-lr">
            Legal review status
          </label>
          <SearchableFilterSelect
            id="adm-ast-lr"
            optionsListLabelledBy="adm-ast-lr-lbl"
            value={legalStatusFilter}
            onChange={setLegalStatusFilter}
            options={legalStatusSelectOptions}
            placeholder="Any review status"
          />
        </FilterField>
        <FilterField>
          <label id="adm-ast-contract-lbl" htmlFor="adm-ast-contract">
            NFT contract
          </label>
          <SearchableFilterSelect
            id="adm-ast-contract"
            optionsListLabelledBy="adm-ast-contract-lbl"
            value={contractFilter}
            onChange={setContractFilter}
            options={contractSelectOptions}
            placeholder="All"
          />
        </FilterField>
        <FilterField>
          <label id="adm-ast-meta-lbl" htmlFor="adm-ast-meta">
            Metadata IPFS
          </label>
          <SearchableFilterSelect
            id="adm-ast-meta"
            optionsListLabelledBy="adm-ast-meta-lbl"
            value={metadataFilter}
            onChange={setMetadataFilter}
            options={metadataSelectOptions}
            placeholder="All"
          />
        </FilterField>
        <FilterField>
          <label id="adm-ast-docs-lbl" htmlFor="adm-ast-docs">
            Documents
          </label>
          <SearchableFilterSelect
            id="adm-ast-docs"
            optionsListLabelledBy="adm-ast-docs-lbl"
            value={docsFilter}
            onChange={setDocsFilter}
            options={docsSelectOptions}
            placeholder="All"
          />
        </FilterField>
        <FilterField>
          <label id="adm-ast-mint-lbl" htmlFor="adm-ast-mint">
            Mint stage
          </label>
          <SearchableFilterSelect
            id="adm-ast-mint"
            optionsListLabelledBy="adm-ast-mint-lbl"
            value={mintStageFilter}
            onChange={setMintStageFilter}
            options={mintStageSelectOptions}
            placeholder="All"
          />
        </FilterField>
        {entries.length > 0 && (
          <FilterMeta>
            Showing {filtered.length} of {entries.length}
          </FilterMeta>
        )}
      </FilterBar>

      {!loading && entries.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#a0a0a0' }}>
          <FiLayers style={{ fontSize: '4rem', marginBottom: '1rem', color: '#4facfe' }} />
          <h3 style={{ color: '#fff' }}>No assets in the registry</h3>
          <p>Assets appear here when tokenizers create them from Core Tokenizer.</p>
        </div>
      )}

      {entries.length > 0 && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#a0a0a0' }}>
          <FiSearch style={{ fontSize: '4rem', marginBottom: '1rem', color: '#4facfe' }} />
          <h3 style={{ color: '#fff' }}>No matching assets</h3>
          <p>Relax filters or clear search to see more results.</p>
        </div>
      )}

      {filtered.length > 0 && registryView === 'gallery' && (
        <AssetGrid>
          {filtered.map(({ raw, mapped }, index) => {
            const docCount = Array.isArray(mapped.documents) ? mapped.documents.length : 0;
            const tokName = resolveTokenizerDisplay(raw, mapped);
            const acLine = galleryAssetClassLine(mapped);
            return (
              <AssetCard
                key={mapped.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.4) }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <div style={{ minWidth: 0 }}>
                    <CardTitle>{mapped.name || mapped.title || 'Untitled'}</CardTitle>
                    <CardMeta>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                        <FiHash style={{ opacity: 0.5 }} />
                        {mapped.assetNumber || '—'}
                      </span>
                      {' · '}
                      <FiMapPin style={{ opacity: 0.5, verticalAlign: 'middle' }} />
                      {mapped.country || '—'}
                    </CardMeta>
                  </div>
                  <StatusPill>{formatEnumLabel(assetStatusCanonical(mapped.status))}</StatusPill>
                </div>
                <BadgeRow>
                  <Badge className="accent">{formatSectorLabel(mapped.sector)}</Badge>
                  {mapped.contractAddress ? <Badge className="ok">Contract deployed</Badge> : <Badge>No class contract</Badge>}
                  {mapped.metadataIpfsCid ? <Badge className="ok">Metadata IPFS</Badge> : null}
                  <Badge>
                    <FiFileText style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    {docCount} doc{docCount === 1 ? '' : 's'}
                  </Badge>
                </BadgeRow>
                <CardMeta style={{ marginBottom: '0.85rem' }}>
                  <div style={{ marginBottom: '0.45rem', lineHeight: 1.45 }}>
                    <strong style={{ color: '#cbd5e1' }}>Asset class</strong>{' '}
                    <span
                      style={{
                        color: '#94a3b8',
                        fontWeight: 400,
                        fontFamily:
                          mapped.assetClassId && !mapped.assetClassName
                            ? 'ui-monospace, monospace'
                            : undefined,
                        fontSize: mapped.assetClassId && !mapped.assetClassName ? '0.82rem' : undefined
                      }}
                      title={mapped.assetClassId ? `Class ID: ${mapped.assetClassId}` : undefined}
                    >
                      {acLine.text}
                    </span>
                  </div>
                  {isAdmin ? (
                    <>
                      <strong style={{ color: '#cbd5e1' }}>Tokenizer</strong> {tokName}
                      <br />
                    </>
                  ) : null}
                  <strong style={{ color: '#cbd5e1' }}>Legal</strong> {summarizeLegalForCard(raw)}
                </CardMeta>
                <CardActions>
                  <CardBtn type="button" onClick={() => openDetail(mapped.id)}>
                    <FiEye /> View details
                  </CardBtn>
                  {(isAuthorizedTokenizer || isAdmin) &&
                  assetStatusCanonical(mapped.status) === 'ApprovedForTokenization' ? (
                    <OutlineCardBtn type="button" onClick={() => openTokenizeWizard(mapped)}>
                      <FiLayers /> Tokenize
                    </OutlineCardBtn>
                  ) : null}
                  {registryShowsFractionalizeAction(mapped, mayTokenizerOrAdmin) ? (
                    <OutlineCardBtn type="button" onClick={() => setFractionalizeMapped(mapped)}>
                      <FiLock /> {fractionalizeRegistryButtonLabel(mapped)}
                    </OutlineCardBtn>
                  ) : null}
                  {mapped.contractAddress && explorerConfigured ? (
                    <IconGhost
                      type="button"
                      title="Contract in explorer"
                      onClick={() => {
                        const url = addressExplorerUrl(mapped.contractAddress);
                        if (url) window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <FiExternalLink />
                    </IconGhost>
                  ) : null}
                  {explorerConfigured ? (
                    <IconGhost
                      type="button"
                      title={
                        nftExplorerLoadingId === mapped.id
                          ? 'Loading mint record…'
                          : 'View minted token in block explorer'
                      }
                      disabled={nftExplorerLoadingId === mapped.id}
                      onClick={() => openMintedAssetInExplorer(mapped.id)}
                    >
                      <FiPackage />
                    </IconGhost>
                  ) : null}
                </CardActions>
              </AssetCard>
            );
          })}
        </AssetGrid>
      )}

      {filtered.length > 0 && registryView === 'table' && (
        <DataTableWrap>
          <DataTable>
            <thead>
              <tr>
                <th>Asset</th>
                <th>Asset #</th>
                <th>Sector</th>
                <th>Asset class</th>
                <th>Status</th>
                {isAdmin ? <th>Tokenizer</th> : null}
                <th>Legal</th>
                <th>Docs</th>
                <th>Contract</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(({ raw, mapped }) => {
                const docCount = Array.isArray(mapped.documents) ? mapped.documents.length : 0;
                const tokName = resolveTokenizerDisplay(raw, mapped);
                const legalLine = summarizeLegalForCard(raw);
                return (
                  <tr key={mapped.id}>
                    <td>
                      <TableAssetTitle>{mapped.name || mapped.title || 'Untitled'}</TableAssetTitle>
                      <TableMuted title={mapped.id}>{String(mapped.id).slice(0, 10)}…</TableMuted>
                    </td>
                    <td>
                      <TableMuted>{mapped.assetNumber || '—'}</TableMuted>
                    </td>
                    <td>{formatSectorLabel(mapped.sector)}</td>
                    <td style={{ maxWidth: 200 }} title={mapped.assetClassName || undefined}>
                      {mapped.assetClassName ? clipText(mapped.assetClassName, 40) : '—'}
                    </td>
                    <td>
                      <StatusPill style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }}>
                        {formatEnumLabel(assetStatusCanonical(mapped.status))}
                      </StatusPill>
                    </td>
                    {isAdmin ? (
                      <td style={{ maxWidth: 200 }} title={tokName}>
                        {clipText(tokName, 36)}
                      </td>
                    ) : null}
                    <td style={{ maxWidth: 220, color: '#94a3b8', fontSize: '0.8rem' }} title={legalLine}>
                      {clipText(legalLine, 52)}
                    </td>
                    <td>{docCount}</td>
                    <td>{mapped.contractAddress ? <span style={{ color: '#86efac' }}>Deployed</span> : '—'}</td>
                    <td>
                      <TableActions>
                        <TableIconBtn type="button" title="View details" onClick={() => openDetail(mapped.id)}>
                          <FiEye />
                        </TableIconBtn>
                        {(isAuthorizedTokenizer || isAdmin) &&
                        assetStatusCanonical(mapped.status) === 'ApprovedForTokenization' ? (
                          <TableIconBtn
                            type="button"
                            title="Tokenize"
                            onClick={() => openTokenizeWizard(mapped)}
                          >
                            <FiLayers />
                          </TableIconBtn>
                        ) : null}
                        {registryShowsFractionalizeAction(mapped, mayTokenizerOrAdmin) ? (
                          <TableIconBtn
                            type="button"
                            title={fractionalizeRegistryButtonLabel(mapped)}
                            onClick={() => setFractionalizeMapped(mapped)}
                          >
                            <FiLock />
                          </TableIconBtn>
                        ) : null}
                        <TableIconBtn
                          type="button"
                          title="Contract in explorer"
                          disabled={!mapped.contractAddress || !explorerConfigured}
                          onClick={() => {
                            const url = addressExplorerUrl(mapped.contractAddress);
                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <FiExternalLink />
                        </TableIconBtn>
                        <TableIconBtn
                          type="button"
                          title={
                            nftExplorerLoadingId === mapped.id
                              ? 'Loading mint record…'
                              : 'Minted token in explorer'
                          }
                          disabled={!explorerConfigured || nftExplorerLoadingId === mapped.id}
                          onClick={() => openMintedAssetInExplorer(mapped.id)}
                        >
                          <FiPackage />
                        </TableIconBtn>
                      </TableActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </DataTable>
        </DataTableWrap>
      )}

      {filtered.length > 0 && registryView === 'compact' && (
        <CompactList>
          {filtered.map(({ raw, mapped }, index) => {
            const docCount = Array.isArray(mapped.documents) ? mapped.documents.length : 0;
            const tokName = resolveTokenizerDisplay(raw, mapped);
            return (
              <CompactRow
                key={mapped.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Math.min(index * 0.02, 0.25) }}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="cr-head">
                    <span className="cr-title">{mapped.name || mapped.title || 'Untitled'}</span>
                    <StatusPill style={{ fontSize: '0.65rem', padding: '0.15rem 0.45rem' }}>
                      {formatEnumLabel(assetStatusCanonical(mapped.status))}
                    </StatusPill>
                  </div>
                  <div className="cr-meta">
                    <span>
                      <FiHash style={{ opacity: 0.45, verticalAlign: 'middle' }} /> {mapped.assetNumber || '—'}
                    </span>
                    <span className="cr-dot">·</span>
                    <span>{formatSectorLabel(mapped.sector)}</span>
                    {mapped.assetClassName ? (
                      <>
                        <span className="cr-dot">·</span>
                        <span title={mapped.assetClassName}>{clipText(mapped.assetClassName, 32)}</span>
                      </>
                    ) : null}
                    <span className="cr-dot">·</span>
                    <span>
                      <FiMapPin style={{ opacity: 0.45, verticalAlign: 'middle' }} /> {mapped.country || '—'}
                    </span>
                    {isAdmin ? (
                      <>
                        <span className="cr-dot">·</span>
                        <span title={tokName}>{clipText(tokName, 28)}</span>
                      </>
                    ) : null}
                    <span className="cr-dot">·</span>
                    <span>
                      <FiFileText style={{ opacity: 0.45, verticalAlign: 'middle' }} /> {docCount}
                    </span>
                    {mapped.contractAddress ? (
                      <>
                        <span className="cr-dot">·</span>
                        <span style={{ color: '#86efac' }}>On-chain</span>
                      </>
                    ) : null}
                  </div>
                  <div
                    style={{
                      marginTop: '0.35rem',
                      fontSize: '0.72rem',
                      color: '#64748b',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={summarizeLegalForCard(raw)}
                  >
                    {summarizeLegalForCard(raw)}
                  </div>
                </div>
                <div className="cr-actions">
                  <CardBtn
                    type="button"
                    style={{ flex: 'none', padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                    onClick={() => openDetail(mapped.id)}
                  >
                    <FiEye /> Details
                  </CardBtn>
                  {(isAuthorizedTokenizer || isAdmin) &&
                  assetStatusCanonical(mapped.status) === 'ApprovedForTokenization' ? (
                    <OutlineCardBtn
                      type="button"
                      style={{ flex: 'none', padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      onClick={() => openTokenizeWizard(mapped)}
                    >
                      <FiLayers /> Tokenize
                    </OutlineCardBtn>
                  ) : null}
                  {registryShowsFractionalizeAction(mapped, mayTokenizerOrAdmin) ? (
                    <OutlineCardBtn
                      type="button"
                      style={{ flex: 'none', padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      onClick={() => setFractionalizeMapped(mapped)}
                    >
                      <FiLock /> {fractionalizeRegistryButtonLabel(mapped)}
                    </OutlineCardBtn>
                  ) : null}
                  {mapped.contractAddress && explorerConfigured ? (
                    <IconGhost
                      type="button"
                      title="Explorer"
                      onClick={() => {
                        const url = addressExplorerUrl(mapped.contractAddress);
                        if (url) window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <FiExternalLink />
                    </IconGhost>
                  ) : null}
                  {explorerConfigured ? (
                    <IconGhost
                      type="button"
                      title={
                        nftExplorerLoadingId === mapped.id
                          ? 'Loading mint record…'
                          : 'Minted token in explorer'
                      }
                      disabled={nftExplorerLoadingId === mapped.id}
                      onClick={() => openMintedAssetInExplorer(mapped.id)}
                    >
                      <FiPackage />
                    </IconGhost>
                  ) : null}
                </div>
              </CompactRow>
            );
          })}
        </CompactList>
      )}

      <AnimatePresence>
        {dm.open && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <ModalCard
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHead>
                <h2>Asset details</h2>
                <IconGhost type="button" onClick={closeModal} aria-label="Close">
                  <FiX />
                </IconGhost>
              </ModalHead>
              <TabRow>
                <TabBtn
                  type="button"
                  $on={dm.tab === 'overview'}
                  onClick={() => setDetailModal((m) => ({ ...m, tab: 'overview' }))}
                >
                  <FiLayers /> Overview
                </TabBtn>
                <TabBtn
                  type="button"
                  $on={dm.tab === 'onchain'}
                  onClick={() => setDetailModal((m) => ({ ...m, tab: 'onchain' }))}
                >
                  <FiPackage /> On-chain
                </TabBtn>
                <TabBtn
                  type="button"
                  $on={dm.tab === 'documents'}
                  onClick={() => setDetailModal((m) => ({ ...m, tab: 'documents' }))}
                >
                  <FiFileText /> Documents
                </TabBtn>
              </TabRow>
              <ModalBody>
                {dm.loading && <p style={{ color: '#94a3b8' }}>Loading…</p>}
                {dm.error && !dm.loading && <p style={{ color: '#fca5a5' }}>{dm.error}</p>}
                {!dm.loading && !dm.error && dm.mapped && dm.tab === 'overview' && (
                  <>
                    <DetailGrid>
                      <dt>Title</dt>
                      <dd>{dm.mapped.name || dm.mapped.title || '—'}</dd>
                      <dt>Asset #</dt>
                      <dd>{dm.mapped.assetNumber || '—'}</dd>
                      <dt>Category / sector</dt>
                      <dd>{formatSectorLabel(dm.mapped.sector)}</dd>
                      <dt>API sector</dt>
                      <dd>{dm.mapped.sector || '—'}</dd>
                      <dt>Status</dt>
                      <dd>{formatEnumLabel(assetStatusCanonical(dm.mapped.status))}</dd>
                      <dt>Country</dt>
                      <dd>{dm.mapped.country || '—'}</dd>
                      <dt>Valuation (USD)</dt>
                      <dd>
                        {dm.mapped.valuationUSD != null
                          ? Number(dm.mapped.valuationUSD).toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : '—'}
                      </dd>
                      <dt>Legal owner</dt>
                      <dd>{dm.mapped.custodian || '—'}</dd>
                      <dt>Owner identifier</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{dm.mapped.ownerAddress || '—'}</dd>
                      <dt>Location</dt>
                      <dd>{dm.mapped.location || '—'}</dd>
                      <dt>Asset class</dt>
                      <dd>{pick(ac, 'name', 'Name') || '—'}</dd>
                      <dt>Asset class ID</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{dm.mapped.assetClassId || '—'}</dd>
                      <dt>Authorized tokenizer</dt>
                      <dd>{pick(tok, 'organizationName', 'OrganizationName') || '—'}</dd>
                      <dt>Tokenizer ID</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{dm.mapped.authorizedTokenizerId || '—'}</dd>
                      <dt>Metadata IPFS CID</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{dm.mapped.metadataIpfsCid || '—'}</dd>
                      <dt>Created (UTC)</dt>
                      <dd>
                        {dm.raw?.createdAtUtc || dm.raw?.CreatedAtUtc
                          ? new Date(dm.raw.createdAtUtc || dm.raw.CreatedAtUtc).toLocaleString()
                          : '—'}
                      </dd>
                      <dt>Updated (UTC)</dt>
                      <dd>
                        {dm.raw?.updatedAtUtc || dm.raw?.UpdatedAtUtc
                          ? new Date(dm.raw.updatedAtUtc || dm.raw.UpdatedAtUtc).toLocaleString()
                          : '—'}
                      </dd>
                      <dt>Asset ID</dt>
                      <dd style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{dm.mapped.id}</dd>
                      <dt>Legal reviews</dt>
                      <dd>
                        {legalReviewsOf(dm.raw).length === 0 ? (
                          'None'
                        ) : (
                          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                            {legalReviewsOf(dm.raw).map((lr) => (
                              <li key={lr.id || lr.Id}>
                                {formatEnumLabel(legalStatusCanonical(lr.status ?? lr.Status))}
                                {reviewerId(lr) ? ` · ${reviewerLabel(reviewerId(lr))}` : ' · Unassigned'}
                              </li>
                            ))}
                          </ul>
                        )}
                      </dd>
                    </DetailGrid>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                      Metadata draft (JSON)
                    </div>
                    <JsonBlock>{JSON.stringify(meta, null, 2)}</JsonBlock>
                  </>
                )}
                {!dm.loading && !dm.error && dm.mapped && dm.tab === 'onchain' && (
                  <>
                    <DetailGrid>
                      <dt>Token standard</dt>
                      <dd>{dm.mapped.tokenStandard || '—'}</dd>
                      <dt>Deployed contract</dt>
                      <dd>
                        {deployedContract ? (
                          <>
                            <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                              {deployedContract}
                            </div>
                            <div style={{ marginTop: '0.5rem' }}>
                              <ChainBtn
                                type="button"
                                disabled={!explorerConfigured}
                                onClick={() => {
                                  const url = addressExplorerUrl(deployedContract);
                                  if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                }}
                              >
                                <FiExternalLink /> View contract
                              </ChainBtn>
                            </div>
                          </>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>Not deployed for this asset class</span>
                        )}
                      </dd>
                      <dt>Minted token</dt>
                      <dd>
                        {dm.mintedTokenLoading && <span style={{ color: '#94a3b8' }}>Loading…</span>}
                        {!dm.mintedTokenLoading && dm.mintedTokenError && (
                          <span style={{ color: '#fca5a5' }}>{dm.mintedTokenError}</span>
                        )}
                        {!dm.mintedTokenLoading && !dm.mintedTokenError && !mint && (
                          <span style={{ color: '#94a3b8' }}>No mint record</span>
                        )}
                        {!dm.mintedTokenLoading && !dm.mintedTokenError && mint && (
                          <>
                            <div style={{ fontSize: '0.88rem', lineHeight: 1.55 }}>
                              <div>
                                Type <strong>{mintStandard || '—'}</strong>
                              </div>
                              {mintTokenId ? (
                                <div style={{ fontFamily: 'monospace' }}>Token ID {mintTokenId}</div>
                              ) : null}
                              {mintAmount ? (
                                <div style={{ fontFamily: 'monospace' }}>Amount {mintAmount}</div>
                              ) : null}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginTop: '0.55rem' }}>
                              <ChainBtn
                                type="button"
                                disabled={!explorerConfigured || !tokenPageContract}
                                onClick={() => {
                                  const url = tokenExplorerUrl(tokenPageContract, mintTokenId);
                                  if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                }}
                              >
                                <FiPackage /> View token
                              </ChainBtn>
                              {mintTx ? (
                                <ChainBtn
                                  type="button"
                                  disabled={!explorerConfigured}
                                  onClick={() => {
                                    const url = transactionExplorerUrl(mintTx);
                                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                  }}
                                >
                                  <FiExternalLink /> Mint tx
                                </ChainBtn>
                              ) : null}
                            </div>
                          </>
                        )}
                      </dd>
                      {isMappedAssetClassErc721(dm.mapped) &&
                        MINTED_OR_LATER.has(assetStatusCanonical(dm.mapped.status)) && (
                          <>
                            <dt>Vault lock</dt>
                            <dd>
                              {detailVaultFrac.loading && (
                                <span style={{ color: '#94a3b8' }}>Loading vault record…</span>
                              )}
                              {!detailVaultFrac.loading && detailVaultFrac.error && (
                                <span style={{ color: '#fca5a5' }}>{detailVaultFrac.error}</span>
                              )}
                              {!detailVaultFrac.loading &&
                                !detailVaultFrac.error &&
                                !detailVaultFrac.vaultLock && (
                                  <span style={{ color: '#94a3b8' }}>No vault lock on file yet</span>
                                )}
                              {!detailVaultFrac.loading && detailVaultFrac.vaultLock && (
                                <div style={{ fontSize: '0.88rem', lineHeight: 1.55 }}>
                                  <div>
                                    Status{' '}
                                    <strong>
                                      {formatEnumLabel(
                                        String(
                                          detailVaultFrac.vaultLock.status ??
                                            detailVaultFrac.vaultLock.Status ??
                                            ''
                                        )
                                      )}
                                    </strong>
                                  </div>
                                  <div style={{ fontFamily: 'monospace', wordBreak: 'break-all', marginTop: 4 }}>
                                    Vault:{' '}
                                    {pick(
                                      detailVaultFrac.vaultLock,
                                      'vaultContractAddress',
                                      'VaultContractAddress'
                                    ) || '—'}
                                  </div>
                                  <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    NFT:{' '}
                                    {pick(
                                      detailVaultFrac.vaultLock,
                                      'nftContractAddress',
                                      'NftContractAddress'
                                    )}{' '}
                                    · ID{' '}
                                    {pick(detailVaultFrac.vaultLock, 'nftTokenId', 'NftTokenId') || '—'}
                                  </div>
                                  {pick(
                                    detailVaultFrac.vaultLock,
                                    'lockTransactionHash',
                                    'LockTransactionHash'
                                  ) ? (
                                    <div style={{ marginTop: '0.45rem' }}>
                                      <ChainBtn
                                        type="button"
                                        disabled={!explorerConfigured}
                                        onClick={() => {
                                          const h = pick(
                                            detailVaultFrac.vaultLock,
                                            'lockTransactionHash',
                                            'LockTransactionHash'
                                          );
                                          const url = transactionExplorerUrl(h);
                                          if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                        }}
                                      >
                                        <FiExternalLink /> Lock transaction
                                      </ChainBtn>
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </dd>
                            <dt>Fractional shares (ERC-20)</dt>
                            <dd>
                              {!detailVaultFrac.loading &&
                                !detailVaultFrac.error &&
                                !detailVaultFrac.fractional && (
                                  <span style={{ color: '#94a3b8' }}>No fractional issuance on file yet</span>
                                )}
                              {!detailVaultFrac.loading && detailVaultFrac.fractional && (
                                <div style={{ fontSize: '0.88rem', lineHeight: 1.55 }}>
                                  <div>
                                    Status{' '}
                                    <strong>
                                      {formatEnumLabel(
                                        String(
                                          detailVaultFrac.fractional.status ??
                                            detailVaultFrac.fractional.Status ??
                                            ''
                                        )
                                      )}
                                    </strong>
                                  </div>
                                  <div>
                                    {pick(detailVaultFrac.fractional, 'tokenName', 'TokenName') || '—'} (
                                    {pick(detailVaultFrac.fractional, 'tokenSymbol', 'TokenSymbol') || '—'})
                                  </div>
                                  <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    Contract:{' '}
                                    {pick(
                                      detailVaultFrac.fractional,
                                      'fractionTokenContractAddress',
                                      'FractionTokenContractAddress'
                                    ) || '—'}
                                  </div>
                                  <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    Minted to:{' '}
                                    {pick(
                                      detailVaultFrac.fractional,
                                      'recipientWallet',
                                      'RecipientWallet'
                                    ) || '—'}
                                  </div>
                                  <div style={{ fontFamily: 'monospace' }}>
                                    Amount (raw):{' '}
                                    {pick(detailVaultFrac.fractional, 'amount', 'Amount') || '—'}
                                  </div>
                                  {pick(
                                    detailVaultFrac.fractional,
                                    'mintTransactionHash',
                                    'MintTransactionHash'
                                  ) ? (
                                    <div style={{ marginTop: '0.45rem' }}>
                                      <ChainBtn
                                        type="button"
                                        disabled={!explorerConfigured}
                                        onClick={() => {
                                          const h = pick(
                                            detailVaultFrac.fractional,
                                            'mintTransactionHash',
                                            'MintTransactionHash'
                                          );
                                          const url = transactionExplorerUrl(h);
                                          if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                        }}
                                      >
                                        <FiExternalLink /> Mint transaction
                                      </ChainBtn>
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </dd>
                          </>
                        )}
                    </DetailGrid>
                    {isMappedAssetClassErc721(dm.mapped) &&
                      MINTED_OR_LATER.has(assetStatusCanonical(dm.mapped.status)) &&
                      mayTokenizerOrAdmin &&
                      registryShowsFractionalizeAction(dm.mapped, mayTokenizerOrAdmin) && (
                        <div style={{ marginTop: '1rem' }}>
                          <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 0.65rem', lineHeight: 1.5 }}>
                            Lock the minted ERC-721 in the vault via offline-signed transactions, then mint ERC-20
                            shares. Vault lock and share issuance are stored on-chain and in the platform database for
                            auditing and future redemption / physical delivery workflows.
                          </p>
                          <ChainBtn type="button" onClick={() => setFractionalizeMapped(dm.mapped)}>
                            <FiLock /> {fractionalizeRegistryButtonLabel(dm.mapped)}
                          </ChainBtn>
                        </div>
                      )}
                  </>
                )}
                {!dm.loading && !dm.error && dm.mapped && dm.tab === 'documents' && (
                  <>
                    {docs.length === 0 ? (
                      <p style={{ color: '#94a3b8' }}>No documents on file.</p>
                    ) : (
                      <DocPanel>
                        <DocTable>
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>File</th>
                              <th>IPFS CID</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {docs.map((d) => {
                              const cid = String(d.ipfsCid || d.IpfsCid || '').trim();
                              const rowKey = assetDocumentRowKey(d);
                              return (
                                <tr key={rowKey}>
                                  <td>{d.documentType || d.DocumentType || '—'}</td>
                                  <td>{d.fileName || d.FileName || '—'}</td>
                                  <td style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{cid || '—'}</td>
                                  <td className="doc-actions">
                                    <DocViewBtn
                                      type="button"
                                      disabled={!cid || openingDocumentKey === rowKey}
                                      onClick={() => openAssetDocument(d)}
                                    >
                                      <FiEye />
                                      {openingDocumentKey === rowKey ? 'Opening…' : 'View'}
                                    </DocViewBtn>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </DocTable>
                      </DocPanel>
                    )}
                  </>
                )}
              </ModalBody>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {createAssetWizardOpen && (
          <CreateAssetWizardModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCreateAssetWizard}
          >
            <CreateAssetWizardModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <AssetCreationWizard
                onClose={closeCreateAssetWizard}
                selectedCategory={createAssetWizardCategory}
              />
            </CreateAssetWizardModalContent>
          </CreateAssetWizardModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {tokenizeModal.open && (
          <TokenizeWizardModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeTokenizeWizard}
          >
            <TokenizeWizardModalContent
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {tokenizeModal.loading || !tokenizeModal.assetClass ? (
                <div
                  style={{
                    padding: '3rem 2rem',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '1rem'
                  }}
                >
                  Loading asset class…
                </div>
              ) : (
                <BatchMintingWizard
                  assetClass={tokenizeModal.assetClass}
                  preselectedAsset={tokenizeModal.mapped}
                  onClose={closeTokenizeWizard}
                />
              )}
            </TokenizeWizardModalContent>
          </TokenizeWizardModalOverlay>
        )}
      </AnimatePresence>

      {fractionalizeMapped ? (
        <VaultFractionalizationModal
          asset={{
            id: fractionalizeMapped.id,
            name: fractionalizeMapped.name || fractionalizeMapped.title || 'Asset',
            status: assetStatusCanonical(fractionalizeMapped.status)
          }}
          onClose={() => setFractionalizeMapped(null)}
          onUpdated={() => {
            load();
            fetchDetailVaultFracForMapped(fractionalizeMapped);
          }}
        />
      ) : null}
    </ManagerContainer>
  );
}

export default TokenizerMyAssets;
